import React, { useRef, useMemo, useEffect } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { OrbitControls as ThreeOrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import * as THREE from 'three'

// Earth component with textures
function Earth() {
  const earthRef = useRef()
  const cloudRef = useRef()
  
  const earthRadius = 2
  
  // Rotate Earth
  useFrame(({ clock }) => {
    if (earthRef.current) {
      earthRef.current.rotation.y = clock.getElapsedTime() * 0.1
    }
    if (cloudRef.current) {
      cloudRef.current.rotation.y = clock.getElapsedTime() * 0.12
    }
  })

  // Create Earth material with more realistic colors
  const earthMaterial = useMemo(() => {
    // Create a simple gradient texture using canvas
    const canvas = document.createElement('canvas')
    canvas.width = 512
    canvas.height = 256
    const ctx = canvas.getContext('2d')
    
    // Create ocean blue base
    ctx.fillStyle = '#1a4d8c'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    
    // Add some landmasses (simplified)
    ctx.fillStyle = '#2d5016'
    // Add random land patches
    for (let i = 0; i < 20; i++) {
      const x = Math.random() * canvas.width
      const y = Math.random() * canvas.height
      const size = 20 + Math.random() * 40
      ctx.beginPath()
      ctx.arc(x, y, size, 0, Math.PI * 2)
      ctx.fill()
    }
    
    const texture = new THREE.CanvasTexture(canvas)
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    
    return new THREE.MeshPhongMaterial({
      map: texture,
      shininess: 5,
      specular: '#666666',
    })
  }, [])

  // Create cloud material
  const cloudMaterial = useMemo(() => {
    const canvas = document.createElement('canvas')
    canvas.width = 512
    canvas.height = 256
    const ctx = canvas.getContext('2d')
    
    // Create cloud pattern
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)'
    for (let i = 0; i < 30; i++) {
      const x = Math.random() * canvas.width
      const y = Math.random() * canvas.height
      const size = 15 + Math.random() * 25
      ctx.beginPath()
      ctx.arc(x, y, size, 0, Math.PI * 2)
      ctx.fill()
    }
    
    const texture = new THREE.CanvasTexture(canvas)
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    
    return new THREE.MeshPhongMaterial({
      map: texture,
      transparent: true,
      opacity: 0.4,
    })
  }, [])

  return (
    <group>
      {/* Earth sphere */}
      <mesh ref={earthRef} position={[0, 0, 0]}>
        <sphereGeometry args={[earthRadius, 128, 64]} />
        <primitive object={earthMaterial} attach="material" />
      </mesh>
      
      {/* Cloud layer */}
      <mesh ref={cloudRef} position={[0, 0, 0]}>
        <sphereGeometry args={[earthRadius + 0.02, 128, 64]} />
        <primitive object={cloudMaterial} attach="material" />
      </mesh>
      
      {/* Atmospheric glow */}
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[earthRadius + 0.15, 64, 64]} />
        <meshBasicMaterial
          color="#0099ff"
          transparent
          opacity={0.15}
          side={THREE.BackSide}
        />
      </mesh>
    </group>
  )
}

// Sun component
function Sun() {
  const sunRef = useRef()
  
  useFrame(() => {
    if (sunRef.current) {
      sunRef.current.rotation.y += 0.001
    }
  })

  return (
    <mesh ref={sunRef} position={[-10, 0, 5]}>
      <sphereGeometry args={[1.5, 32, 32]} />
      <meshBasicMaterial
        color="#ffeecc"
        emissive="#ffeecc"
        emissiveIntensity={2}
      />
      {/* Sun glow */}
      <pointLight
        color="#ffeecc"
        intensity={2}
        distance={50}
        decay={2}
      />
    </mesh>
  )
}

// Custom Stars component to avoid drei dependency issues
function StarsField() {
  const starsRef = useRef()
  
  const stars = useMemo(() => {
    const starCount = 8000
    const positions = new Float32Array(starCount * 3)
    const radius = 300
    
    for (let i = 0; i < starCount; i++) {
      const i3 = i * 3
      // Random spherical distribution
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(Math.random() * 2 - 1)
      const r = radius + Math.random() * 100
      
      positions[i3] = r * Math.sin(phi) * Math.cos(theta)
      positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta)
      positions[i3 + 2] = r * Math.cos(phi)
    }
    
    return positions
  }, [])
  
  useFrame(({ clock }) => {
    if (starsRef.current) {
      starsRef.current.rotation.y = clock.getElapsedTime() * 0.05
    }
  })
  
  return (
    <points ref={starsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={stars.length / 3}
          array={stars}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.5}
        color="#ffffff"
        sizeAttenuation={true}
        transparent
        opacity={0.8}
      />
    </points>
  )
}

// Additional point lights for better illumination
function AdditionalLights() {
  return (
    <>
      <pointLight
        position={[-10, 0, 8]}
        color="#ffffff"
        intensity={0.6}
        distance={40}
        decay={2}
      />
      <pointLight
        position={[-10, 0, -4]}
        color="#ffffff"
        intensity={0.6}
        distance={40}
        decay={2}
      />
      <pointLight
        position={[-10, 4, 2]}
        color="#ffffff"
        intensity={0.6}
        distance={40}
        decay={2}
      />
      <pointLight
        position={[-10, -4, 2]}
        color="#ffffff"
        intensity={0.6}
        distance={40}
        decay={2}
      />
    </>
  )
}

// OrbitControls component using native Three.js
function Controls() {
  const { camera, gl } = useThree()
  const controlsRef = useRef()
  
  useEffect(() => {
    const controls = new ThreeOrbitControls(camera, gl.domElement)
    controls.enablePan = true
    controls.enableZoom = true
    controls.enableRotate = true
    controls.minDistance = 6
    controls.maxDistance = 20
    controls.autoRotate = true
    controls.autoRotateSpeed = 0.5
    controls.target.set(0, 0, 0)
    
    controlsRef.current = controls
    
    return () => {
      controls.dispose()
    }
  }, [camera, gl])
  
  useFrame(() => {
    if (controlsRef.current) {
      controlsRef.current.update()
    }
  })
  
  return null
}

// Main scene component
function Scene() {
  return (
    <>
      {/* Ambient light */}
      <ambientLight intensity={0.2} />
      
      {/* Sun and additional lights */}
      <Sun />
      <AdditionalLights />
      
      {/* Stars background */}
      <StarsField />
      
      {/* Earth */}
      <Earth />
      
      {/* Camera controls */}
      <Controls />
    </>
  )
}

// Main Universe3D component
export default function Universe3D({ className = "" }) {
  return (
    <div className={`w-full h-full ${className}`}>
      <Canvas
        camera={{ position: [0, 0, 8], fov: 45 }}
        gl={{ antialias: true, alpha: true }}
        style={{ background: '#000000' }}
      >
        <Scene />
      </Canvas>
    </div>
  )
}

